'use client';

import React, { useCallback, memo, useState } from 'react';
import { format, differenceInHours } from 'date-fns';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate,
  faCalendarDays,
  faCheck,
  faChevronDown,
  faClock,
  faCloudArrowDown,
  faExclamationTriangle,
  faFileCsv,
  faFileExcel,
  faOctagonExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { useRerunExportMutation } from 'src/api/redux';
import { useNotification } from 'src/context/notification';
import { Typography } from 'src/components';
import { Export } from '../../types';
import { getColumnWidth } from '../../utils';
interface ExportRowProps {
  item: Export;
  isExpanded: boolean;
  onExpand: (id: number) => void;
}

const ExportRow: React.FC<ExportRowProps> = ({
  item,
  isExpanded,
  onExpand,
}) => {
  const { setNotification } = useNotification();
  const [rerunExport] = useRerunExportMutation();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const isExportExpired = useCallback(
    (createdAt: string): boolean =>
      differenceInHours(new Date(), new Date(createdAt)) >= 24,
    []
  );

  const getExportFilename = useCallback((item: Export): string => {
    if (item.download_url) {
      try {
        const urlParts = item.download_url.split('/');
        const filename = urlParts[urlParts.length - 1]?.split('?')[0] || '';

        if (filename) {
          return (
            filename
              .replace(/\.\w+$/, '')
              // .replace(/(_|\s)\d{4}-\d{2}-\d{2}.*$/, '') //TODO: leaving incase we need to remove date
              .replace(/_/g, ' ')
              .replace(/([A-Z])/g, ' $1')
              .trim()
          );
        }
      } catch (error) {
        // No action needed
      }
    }

    return 'Export Name Unavailable';
  }, []);

  const handleReInitiateDownload = useCallback(
    async (item: Export) => {
      setIsRefreshing(true);
      try {
        // The rerun endpoint replays the original params server-side by id,
        // for both VIEW and TERRITORY_BULK exports — no client-side payload.
        await rerunExport({ id: item.id }).unwrap();

        setNotification({
          title: 'Success',
          message: 'Export re-initiated successfully.',
          type: 'success',
        });
      } catch (err) {
        setNotification({
          title: 'Error',
          message:
            err?.data?.message ??
            err?.data?.error_message ??
            'Export failed to re-initiate.',
          type: 'error',
        });
      } finally {
        setIsRefreshing(false);
      }
    },
    [rerunExport, setNotification]
  );

  const handleDownload = useCallback(() => {
    if (
      isExportExpired(item.created_at) ||
      item.status !== 'SUCCESS' ||
      !item.download_url
    )
      return;
    window.open(item.download_url, '_blank');
  }, [item, isExportExpired]);

  const renderStatus = () => {
    const isExpired = isExportExpired(item.created_at);

    if (isExpired) {
      return (
        <span className="flex items-center gap-2 text-sm font-medium text-amber-600 bg-amber-50 rounded-full">
          <Icon
            icon={faOctagonExclamation}
            className="w-4 h-4"
            aria-hidden="true"
          />
          <span>Expired</span>
        </span>
      );
    } else if (item.status === 'SUCCESS') {
      return (
        <div className="flex items-center gap-2 text-sm text-green-600">
          <Icon icon={faCheck} className="animate-pulse" aria-hidden="true" />
          <span>Success</span>
        </div>
      );
    } else if (item.status === 'PENDING') {
      return (
        <div className="flex items-center gap-2 text-sm text-amber-600">
          <Icon icon={faClock} className="animate-pulse" aria-hidden="true" />
          <span>Processing</span>
        </div>
      );
    } else if (item.status === 'FAILED') {
      return (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <Icon icon={faExclamationTriangle} aria-hidden="true" />
          <span>Error fetching data</span>
        </div>
      );
    }

    return null;
  };

  const filename = getExportFilename(item);

  // Normalize columns: API may return an object or null instead of string[]
  const columns: string[] = Array.isArray(item.columns)
    ? item.columns.filter((col): col is string => typeof col === 'string')
    : [];

  return (
    <tr
      className="hover:bg-neutral-50/80 transition-colors duration-150"
      role="row"
    >
      <td
        className={`px-2 pl-4 py-4 whitespace-nowrap ${getColumnWidth('type')}`}
        role="cell"
      >
        <div
          className={`w-6 h-6 flex items-center justify-center rounded transition-colors duration-200 ${
            item.file_format === 'csv'
              ? 'bg-blue-50 text-blue-600'
              : 'bg-green-50 text-green-600'
          }`}
        >
          <Icon
            icon={item.file_format === 'csv' ? faFileCsv : faFileExcel}
            className="w-4 h-4"
            aria-hidden="true"
            data-testid={
              item.file_format === 'csv' ? 'file-csv-icon' : 'file-excel-icon'
            }
          />
        </div>
      </td>

      <td className={`px-2 py-4 ${getColumnWidth('name')}`} role="cell">
        <div className="flex items-center gap-3">
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2">
              <Typography className="font-medium text-neutral-900 truncate">
                {filename}
              </Typography>
            </div>

            <div className="flex flex-wrap gap-1 mt-1">
              {columns
                .slice(0, isExpanded ? undefined : 2)
                .map((column, index) => (
                  <span
                    key={index}
                    className="px-2 py-0.5 text-xs font-medium text-neutral-600 bg-neutral-100 rounded-full truncate max-w-[180px] hover:bg-neutral-200 transition-colors duration-150"
                    title={column}
                  >
                    {column}
                  </span>
                ))}
              {!isExpanded && columns.length > 2 && (
                <button
                  onClick={() => onExpand(item.id)}
                  className="px-2 py-0.5 text-xs font-medium text-neutral-600 bg-neutral-100 rounded-full hover:bg-neutral-200 transition-all duration-150 flex items-center gap-1 shrink-0 hover:scale-105"
                  aria-label={`Show all ${columns.length} columns`}
                >
                  +{columns.length - 2} more
                  <Icon
                    icon={faChevronDown}
                    className="h-3 w-3 transition-transform duration-150"
                    aria-hidden="true"
                  />
                </button>
              )}
              {isExpanded && (
                <button
                  onClick={() => onExpand(item.id)}
                  className="px-2 py-0.5 text-xs font-medium text-neutral-600 bg-neutral-100 rounded-full hover:bg-neutral-200 transition-all duration-150 flex items-center gap-1 shrink-0 hover:scale-105"
                  aria-label="Show fewer columns"
                >
                  Show less
                  <Icon
                    icon={faChevronDown}
                    className="h-3 w-3 transition-transform duration-150 rotate-180"
                    aria-hidden="true"
                  />
                </button>
              )}
            </div>
          </div>
        </div>
      </td>
      <td
        className={`px-2 py-4 whitespace-nowrap ${getColumnWidth('status')}`}
        role="cell"
      >
        <div className="flex items-center gap-2">{renderStatus()}</div>
      </td>
      <td
        className={`px-2 py-4 whitespace-nowrap ${getColumnWidth('created')}`}
        role="cell"
      >
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <Icon
            icon={faCalendarDays}
            className="text-neutral-400"
            aria-hidden="true"
          />
          <span>{format(new Date(item.created_at), 'MMM d, yyyy h:mm a')}</span>
        </div>
      </td>
      <td
        className="px-2 pr-4 py-4 whitespace-nowrap text-right w-24"
        role="cell"
      >
        <div className="flex items-center justify-end gap-2">
          {(item.is_download_valid ?? true) &&
            !isExportExpired(item.created_at) &&
            item.download_url && (
            <button
              onClick={handleDownload}
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200 ${
                item.status === 'PENDING'
                  ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                  : 'bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 hover:scale-110'
              }`}
              disabled={item.status === 'PENDING'}
              aria-label={`Download ${filename} export`}
              data-testid={`download-button-${item.id}`}
            >
              <Icon icon={faCloudArrowDown} aria-hidden="true" />
            </button>
          )}
          {(isExportExpired(item.created_at) || item.status === 'PENDING') && (
            <button
              onClick={() => handleReInitiateDownload(item)}
              disabled={isRefreshing || item.status === 'PENDING'}
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200
                ${
                  isRefreshing || item.status === 'PENDING'
                    ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                    : 'bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700 hover:scale-110'
                }`}
              aria-label={`Refresh download link for ${filename}`}
              data-testid={`refresh-button-${item.id}`}
            >
              <Icon
                icon={faArrowsRotate}
                className={
                  isRefreshing || item.status === 'PENDING'
                    ? 'animate-spin'
                    : ''
                }
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

export default memo(ExportRow);
